import React from 'react';
import Load, { Charge } from '../../../models/load';
import invoiceClasses from '../Invoice/Invoice.module.scss';
import { format } from 'date-fns';
import { Document, Page, View, Text, Image, StyleSheet, PDFDownloadLink } from '@react-pdf/renderer';
import logo from '../../../assets/images/logo.png';
import { FaDownload } from 'react-icons/fa';

interface ClientInvoiceProps {
  load: Load;
  close: () => void;
  priceList: { [key: string]: any };
}

// PDF Styles
const styles = StyleSheet.create({
  page: {
    fontSize: 12,
    fontFamily: 'Helvetica',
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    borderBottom: 1,
    paddingBottom: 10,
  },
  logo: {
    width: 120,
    height: 40,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
  },
  section: {
    marginBottom: 20,
  },
  loadInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  loadDetail: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  label: {
    fontWeight: 'bold',
    marginRight: 5,
    minWidth: 80,
  },
  table: {
    border: 1,
    borderColor: '#000',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: 1,
    borderColor: '#000',
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold',
  },
  tableCell: {
    padding: 8,
    flex: 1,
    borderRight: 1,
    borderColor: '#000',
  },
  tableCellLast: {
    padding: 8,
    flex: 1,
  },
  totalRow: {
    backgroundColor: '#f9f9f9',
    fontWeight: 'bold',
  }
});

// PDF Document Component
const ClientInvoiceDocument: React.FC<{ load: Load; priceList: { [key: string]: any } }> = ({ load, priceList }) => {
  const getLoadPointPrice = (): number => {
    if (load.loadPoint1 != null) {
      const price = priceList[load.loadPoint1];
      return price * (1 + getFuelLevy()) || 0;
    }
    return 0;
  };

  const getGensetPrice = (): number => {
    if (load.isGenset == true) {
      const price = priceList['additionalCharges']['genset'];
      return price || 0;
    }
    return 0;
  };

  const getOnRoutePrice = (): number => {
    if (load.isGenset == true) {
      const price = priceList['additionalCharges']['dualLoadOnRoute'];
      return price || 0;
    }
    return 0;
  };

  const getChargesTotal = (): number => {
    return JSON.parse(load.charges ?? '[]').reduce((acc: number, charge: Charge) => acc + parseFloat(charge.amount), 0);
  };

  const getFuelLevy = (): number => {
    return priceList['fuelLevyPercentage'] / 100;
  };

  const getTotalAmount = (): number => {
    return getLoadPointPrice() + getGensetPrice() + getOnRoutePrice() + getChargesTotal();
  };

  const charges = JSON.parse(load.charges ?? '[]');

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <Text style={styles.title}>INVOICE</Text>
        </View>

        <View style={styles.loadInfo}>
          <View>
            <View style={styles.loadDetail}>
              <Text style={styles.label}>Load Ref:</Text>
              <Text>{load.clientRef || '-'}</Text>
            </View>
            <View style={styles.loadDetail}>
              <Text style={styles.label}>Container:</Text>
              <Text>{load.containerNumber || '-'}</Text>
            </View>
            <View style={styles.loadDetail}>
              <Text style={styles.label}>Load Date:</Text>
              <Text>
                {load.loadDate
                  ? format(new Date(load.loadDate), 'dd/MM/yyyy')
                  : '-'}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={styles.tableCell}>Description</Text>
            <Text style={styles.tableCell}>Details</Text>
            <Text style={styles.tableCell}>Quantity</Text>
            <Text style={styles.tableCellLast}>Amount</Text>
          </View>

          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>Load Point 1</Text>
            <Text style={styles.tableCell}>{load.loadPoint1}</Text>
            <Text style={styles.tableCell}>1</Text>
            <Text style={styles.tableCellLast}>R {getLoadPointPrice().toFixed(2)}</Text>
          </View>

          {getGensetPrice() > 0 && (
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>Genset</Text>
              <Text style={styles.tableCell}>{load.isGenset ? 'Yes' : 'No'}</Text>
              <Text style={styles.tableCell}>1</Text>
              <Text style={styles.tableCellLast}>R {getGensetPrice().toFixed(2)}</Text>
            </View>
          )}

          {getOnRoutePrice() > 0 && (
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>Dual Load On Route</Text>
              <Text style={styles.tableCell}>{load.loadPoint2 != null ? load.loadPoint2 : '-'}</Text>
              <Text style={styles.tableCell}>1</Text>
              <Text style={styles.tableCellLast}>R {getOnRoutePrice().toFixed(2)}</Text>
            </View>
          )}

          {charges.map((charge: Charge, index: number) => (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.tableCell}>{charge.description}</Text>
              <Text style={styles.tableCell}></Text>
              <Text style={styles.tableCell}>{charge.quantity}</Text>
              <Text style={styles.tableCellLast}>R {charge.amount}</Text>
            </View>
          ))}

          <View style={[styles.tableRow, styles.totalRow]}>
            <Text style={styles.tableCell}>Total</Text>
            <Text style={styles.tableCell}></Text>
            <Text style={styles.tableCell}></Text>
            <Text style={styles.tableCellLast}>R {getTotalAmount().toFixed(2)}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const ClientInvoice: React.FC<ClientInvoiceProps> = (props) => {
  const getLoadPointPrice = (): number => {
    if (props.load.loadPoint1 != null) {
      const price = props.priceList[props.load.loadPoint1]
      return price * (1 + getFuelLevy());
    }

    return 0;
  }

  const getGensetPrice = (): number => {
    if (props.load.isGenset == true) {
      const price = props.priceList['additionalCharges']['genset']
      return price;
    }

    return 0;
  }

  const getOnRoutePrice = (): number => {
    if (props.load.loadPoint2 != null && props.load.loadPoint2 != '') {
      const price = props.priceList['additionalCharges']['dualLoadOnRoute']
      return price;
    }
    return 0;
  }

  const getChargesTotal = (): number => {
    return JSON.parse(props.load.charges ?? '[]').reduce((acc: number, charge: Charge) => acc + parseFloat(charge.amount), 0);
  }

  const getFuelLevy = (): number => {
    return props.priceList['fuelLevyPercentage'] / 100;
  }

  const getTotalAmount = (): number => {
    return getLoadPointPrice() + getGensetPrice() + getOnRoutePrice() + getChargesTotal();
  }

  return (
    <div className={invoiceClasses.invoiceContainer}>
      <h4 className={invoiceClasses.title}>Invoice</h4>

      <div className={invoiceClasses.loadInfo}>
        <div className={invoiceClasses.loadDetail}>
          <span className={invoiceClasses.label}>Load Ref:</span>
          <span>{props.load.clientRef || '-'}</span>
        </div>
        <div className={invoiceClasses.loadDetail}>
          <span className={invoiceClasses.label}>Container:</span>
          <span>{props.load.containerNumber || '-'}</span>
        </div>
        <div className={invoiceClasses.loadDetail}>
          <span className={invoiceClasses.label}>Load Date:</span>
          <span>
            {props.load.loadDate
              ? format(new Date(props.load.loadDate), 'dd/MM/yyyy')
              : (props.load.loadDate ? format(new Date(props.load.loadDate), 'dd/MM/yyyy') : '-')}
          </span>
        </div>
      </div>

      <table className={invoiceClasses.invoiceTable}>
        <thead>
          <tr>
            <th>Description</th>
            <th>Details</th>
            <th>Quantity</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Load Point 1</td>
            <td>{props.load.loadPoint1}</td>
            <td>1</td>
            <td>R {getLoadPointPrice().toFixed(2)}</td>
          </tr>

          {getGensetPrice() > 0 && (
            <tr>
              <td>Genset</td>
              <td>{props.load.isGenset ? 'Yes' : 'No'}</td>
              <td>1</td>
              <td>R {getGensetPrice().toFixed(2)}</td>
            </tr>
          )}

          {getOnRoutePrice() > 0 && (
            <tr>
              <td>Dual Load On Route</td>
              <td>{props.load.loadPoint2 != null ? props.load.loadPoint2 : '-'}</td>
              <td>1</td>
              <td>R {getOnRoutePrice().toFixed(2)}</td>
            </tr>
          )}

          {JSON.parse(props.load.charges ?? '[]').map((charge: Charge, index: number) => (
            <tr key={index}>
              <td>{charge.description}</td>
              <td></td>
              <td>{charge.quantity}</td>
              <td>R {charge.amount}</td>
            </tr>
          ))}

          <tr>
            <td colSpan={3}><b>Total</b></td>
            <td><b>R {getTotalAmount().toFixed(2)}</b></td>
          </tr>
        </tbody>
      </table>

      <div className='d-flex justify-content-around'>
        <PDFDownloadLink
          document={<ClientInvoiceDocument load={props.load} priceList={props.priceList} />}
          fileName={`ClientInvoice-${props.load.clientRef || props.load.id}.pdf`}
          className='btn btn-primary'
          style={{
            backgroundColor: 'var(--theme-green)',
            border: 'none'
          }}
        >
          {({ loading }) => (
            <>
              <FaDownload />
              {loading ? 'Preparing PDF...' : '  Download'}
            </>
          )}
        </PDFDownloadLink>
      </div>
    </div>
  );
};

export default ClientInvoice;
